import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiBell, FiMenu, FiChevronDown, FiLogOut, FiUser, FiLock, FiHelpCircle, FiGift } from "react-icons/fi";
import api from "../services/api";
import MarketingOfferModal from "./dashboard/MarketingOfferModal";
import ThemeToggle from "./ThemeToggle";
import { useTheme } from "../context/ThemeContext";

const Header = ({ onMenuClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMarketingOpen, setIsMarketingOpen] = useState(false);
  const [marketingProduct, setMarketingProduct] = useState(null);
  const [displayName, setDisplayName] = useState("User");
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const logoSrc = isDark ? "/logo512-white.png" : "/logo512.png";

  useEffect(() => {
    const fetchMe = async () => {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        return;
      }

      try {
        const response = await api.get("/me", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const user = response.data?.data;
        if (user?.username) {
          setDisplayName(user.username);
        }
      } catch (error) {
        return;
      }
    };

    fetchMe();
  }, []);

  const handleMarketingOffer = async () => {
    const token = localStorage.getItem("rmc2.access_token");
    if (!token) {
      return;
    }

    try {
      const response = await api.get("/marketing-offer", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.status !== "success") {
        return;
      }

      const data = response.data?.data || {};
      if (!data.eligible || !data.product) {
        return;
      }

      setMarketingProduct(data.product);
      setIsMarketingOpen(true);
    } catch (error) {
      return;
    }
  };

  const handleLogout = async () => {
    const shouldLogout = window.confirm("Are you sure you want to logout?");
    if (shouldLogout) {
      setIsMenuOpen(false);
      const token = localStorage.getItem("rmc2.access_token");

      if (token) {
        try {
          await api.post(
            "/logout",
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
        } catch (error) {
          console.debug("[Logout] request failed", error?.response?.status);
        }
      }

      localStorage.removeItem("rmc2.access_token");
      localStorage.removeItem("rmc2.refresh_token");
      localStorage.removeItem("rmc2.token_type");
      localStorage.removeItem("rmc2.token_expires_at");
      navigate("/login");
    }
  };

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-white/40 bg-white/80 backdrop-blur">
        <div className="flex h-[72px] w-full items-center px-4 lg:px-6">
          <div className="flex flex-1 items-center justify-start gap-4">
            <button
              type="button"
              onClick={onMenuClick}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/70 text-slate-700 shadow-sm ring-1 ring-slate-200/70 transition hover:text-slate-900 lg:hidden"
              aria-label="Open navigation menu"
            >
              <FiMenu className="text-xl" />
            </button>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-lg ring-1 ring-slate-200/70">
                <img
                  src={logoSrc}
                  alt="RMC2 logo"
                />
              </div>
              <div className="flex items-center gap-2 text-base font-semibold">
                <span className="text-slate-900">RMC2</span>
                <span className="text-[var(--brand-orange-dark)]">
                  Emergency Care (ER)
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-1 items-center justify-end gap-4">
            <ThemeToggle />
            {/*
            <button
              type="button"
              className="relative inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm ring-1 ring-slate-200/70 transition hover:text-slate-900"
              aria-label="Notifications"
            >
              <FiBell className="text-xl" />
              <span className="absolute right-3 top-3 h-2 w-2 rounded-full bg-red-500"></span>
            </button>
            */}
            <button
              type="button"
              onClick={() => navigate("/faqs-view")}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm ring-1 ring-slate-200/70 transition hover:text-slate-900"
              aria-label="FAQs"
            >
              <FiHelpCircle className="text-xl" />
            </button>
            {/*
            <button
              type="button"
              onClick={handleMarketingOffer}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm ring-1 ring-slate-200/70 transition hover:text-slate-900"
              aria-label="Marketing offer"
            >
              <FiGift className="text-xl" />
            </button>
            */}
            <div className="relative z-50">
              <button
                type="button"
                onClick={() => setIsMenuOpen((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200/70 transition hover:text-slate-900"
                aria-haspopup="true"
                aria-expanded={isMenuOpen}
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 shadow-inner">
                  <FiUser className="text-sm" />
                </span>
                <span className="hidden max-w-[140px] truncate text-sm font-semibold lg:inline">
                  {displayName}
                </span>
                <FiChevronDown className="text-sm" />
              </button>
              {isMenuOpen && (
                <div className="absolute right-0 z-50 mt-3 w-48 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate("/profile");
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                  >
                    <FiUser />
                    Profile
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate("/change-password");
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                  >
                    <FiLock />
                    Change Password
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-rose-600 transition hover:bg-rose-50"
                  >
                    <FiLogOut />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      {/*
      <MarketingOfferModal
        open={isMarketingOpen}
        product={marketingProduct}
        onClose={() => setIsMarketingOpen(false)}
        onViewProducts={() => {
          setIsMarketingOpen(false);
          navigate("/products");
        }}
      />
      */}
    </>
  );
};

export default Header;
